import { motion } from "framer-motion";
import {
    Download,
    Mail,
    Image as ImageIcon,
    FileText,
    ExternalLink,
    Copy,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";

const RESOURCES = [
    {
        category: "E-postsignaturer",
        icon: Mail,
        items: [
            {
                title: "Outlook desktop (Windows) — komplett paket",
                desc: "Innehåller .htm-fil + bildmapp. Släpps direkt i %APPDATA%\\Microsoft\\Signatures\\. Mest pålitlig för Outlook 365/2019/2021.",
                href: "/signatures/BM-Juridik-Outlook-Signaturer.zip",
                cta: "Ladda ner ZIP",
                testid: "download-outlook-zip",
            },
            {
                title: "Daniel Madsen — webb-signatur (HTML)",
                desc: "Öppna, markera signaturen och kopiera/klistra in i Outlook Web, Apple Mail eller Gmail. Logotypen är inbäddad i koden.",
                href: "/signatures/daniel.html",
                cta: "Öppna signatur",
                testid: "open-daniel-signature",
                external: true,
            },
            {
                title: "Bianca Madsen — webb-signatur (HTML)",
                desc: "Öppna, markera signaturen och kopiera/klistra in i Outlook Web, Apple Mail eller Gmail. Logotypen är inbäddad i koden.",
                href: "/signatures/bianca.html",
                cta: "Öppna signatur",
                testid: "open-bianca-signature",
                external: true,
            },
            {
                title: "Installationsguide",
                desc: "Detaljerade instruktioner för Outlook desktop, Outlook web, Apple Mail och Gmail.",
                href: "/signatures/INSTALLATION.md",
                cta: "Visa guide",
                testid: "view-signature-guide",
                external: true,
            },
        ],
    },
    {
        category: "Logotyper",
        icon: ImageIcon,
        items: [
            {
                title: "Komplett logotyppaket (ZIP)",
                desc: "Alla varianter: mörk, ljus, vit, monogram. SVG (vektor) + PNG i flera storlekar (300w, 800w, original). För Word, brevpapper, presentationer, sociala medier.",
                href: "/logo/bm-juridik-logos.zip",
                cta: "Ladda ner alla logotyper",
                testid: "download-logo-zip",
            },
            {
                title: "Logotyp — mörk version (PNG)",
                desc: "För användning på ljusa bakgrunder. 800px bredd, transparent bakgrund.",
                href: "/logo/bm-juridik-dark-800w.png",
                cta: "PNG mörk",
                testid: "download-logo-dark-png",
            },
            {
                title: "Logotyp — vit version (PNG)",
                desc: "För användning på mörka bakgrunder och bilder. 800px bredd, transparent bakgrund.",
                href: "/logo/bm-juridik-white-800w.png",
                cta: "PNG vit",
                testid: "download-logo-white-png",
            },
            {
                title: "Monogram (PNG, 256×256)",
                desc: "Använd som favikon, profilbild på sociala medier, eller liten ikon i e-postsignatur.",
                href: "/logo/bm-juridik-monogram-256.png",
                cta: "PNG monogram",
                testid: "download-logo-monogram-png",
            },
            {
                title: "SVG vektor (mörk)",
                desc: "För skalning utan kvalitetsförlust. Använd i tryckmaterial, skyltar, stora format.",
                href: "/logo/bm-juridik-dark.svg",
                cta: "SVG mörk",
                testid: "download-logo-dark-svg",
            },
        ],
    },
    {
        category: "Dokumentation",
        icon: FileText,
        items: [
            {
                title: "Robots.txt (SEO)",
                desc: "Konfiguration för sökmotorer. Visas på live-sajten.",
                href: "/robots.txt",
                cta: "Visa robots.txt",
                testid: "view-robots",
                external: true,
            },
            {
                title: "Sitemap.xml (SEO)",
                desc: "XML-sitemap med alla publika sidor för Google Search Console.",
                href: "/sitemap.xml",
                cta: "Visa sitemap",
                testid: "view-sitemap",
                external: true,
            },
        ],
    },
];

const COPYABLE = [
    {
        label: "Daniel Madsen — e-post",
        value: "daniel@bmjuridik.se",
    },
    {
        label: "Bianca Madsen — e-post",
        value: "bianca@bmjuridik.se",
    },
    {
        label: "Kontakt — generell",
        value: "kontakt@bmjuridik.se",
    },
    {
        label: "Telefon — växel",
        value: "010 - 808 56 13",
    },
    {
        label: "Telefon — Daniel mobil",
        value: "0790 - 52 61 51",
    },
    {
        label: "Adress — Stockholm",
        value: "Sveavägen 31, 111 34 Stockholm",
    },
    {
        label: "Webbplats",
        value: "https://bmjuridik.se",
    },
];

const copyToClipboard = async (text, label) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} kopierad`);
    } catch {
        toast.error("Kunde inte kopiera");
    }
};

export default function Resurser() {
    useSEO({
        title: "Resurser — interna nedladdningar",
        description: "Logotyper, e-postsignaturer och dokument för BM Juridiks medarbetare.",
        noindex: true,
    });

    return (
        <div className="bg-[#0A0A0B] text-[#F4F4F5]" data-testid="resurser-page">
            <PageHeader
                kicker="Internt material"
                title="Resurser"
                description="Logotyper, e-postsignaturer och dokumentation för medarbetare. Alla filer är klara att använda direkt — ingen extern bearbetning krävs."
            />

            <div className="container mx-auto px-6 lg:px-8 py-16 max-w-5xl">
                {/* Resource categories */}
                <div className="space-y-16">
                    {RESOURCES.map((cat) => {
                        const Icon = cat.icon;
                        return (
                            <section key={cat.category} data-testid={`resource-section-${cat.category}`}>
                                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-white/10">
                                    <div className="w-10 h-10 flex items-center justify-center bg-[#C4A977]/10 border border-[#C4A977]/30 rounded-sm">
                                        <Icon className="w-5 h-5 text-[#C4A977]" />
                                    </div>
                                    <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                                        {cat.category}
                                    </h2>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    {cat.items.map((item) => (
                                        <motion.div
                                            key={item.title}
                                            initial={{ opacity: 0, y: 8 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4 }}
                                            className="bg-[#111113] border border-white/10 hover:border-[#C4A977]/40 transition-colors p-6 flex flex-col"
                                            data-testid={item.testid}
                                        >
                                            <h3 className="text-base font-semibold mb-2 text-[#F4F4F5]">
                                                {item.title}
                                            </h3>
                                            <p className="text-sm text-[#A1A1AA] mb-5 flex-1 leading-relaxed">
                                                {item.desc}
                                            </p>
                                            <a
                                                href={item.href}
                                                {...(item.external
                                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                                    : { download: true })}
                                                className="inline-flex items-center gap-2 text-sm font-medium text-[#C4A977] hover:text-[#D4BB8A] border border-[#C4A977]/40 hover:border-[#C4A977] px-4 py-2 rounded-sm transition-colors w-fit"
                                                data-testid={`${item.testid}-link`}
                                            >
                                                {item.external ? (
                                                    <ExternalLink className="w-4 h-4" />
                                                ) : (
                                                    <Download className="w-4 h-4" />
                                                )}
                                                {item.cta}
                                            </a>
                                        </motion.div>
                                    ))}
                                </div>
                            </section>
                        );
                    })}

                    {/* Quick copy section */}
                    <section data-testid="resource-section-quick-copy">
                        <div className="flex items-center gap-3 mb-8 pb-4 border-b border-white/10">
                            <div className="w-10 h-10 flex items-center justify-center bg-[#C4A977]/10 border border-[#C4A977]/30 rounded-sm">
                                <Copy className="w-5 h-5 text-[#C4A977]" />
                            </div>
                            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                                Kontaktuppgifter (kopiera direkt)
                            </h2>
                        </div>

                        <div className="bg-[#111113] border border-white/10 divide-y divide-white/10">
                            {COPYABLE.map((c) => (
                                <button
                                    key={c.label}
                                    onClick={() => copyToClipboard(c.value, c.label)}
                                    className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors text-left group"
                                    data-testid={`copy-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs uppercase tracking-wider text-[#A1A1AA] mb-1">
                                            {c.label}
                                        </div>
                                        <div className="font-mono text-sm text-[#F4F4F5] truncate">
                                            {c.value}
                                        </div>
                                    </div>
                                    <Copy className="w-4 h-4 text-[#A1A1AA] group-hover:text-[#C4A977] flex-shrink-0" />
                                </button>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Footer note */}
                <div className="mt-16 pt-8 border-t border-white/10 text-xs text-[#A1A1AA] leading-relaxed">
                    <p>
                        Denna sida är endast åtkomlig för administratörer. Filerna får
                        endast användas i samband med BM Juridiks officiella verksamhet.
                        Vid frågor om varumärkesriktlinjer, kontakta Daniel eller Bianca.
                    </p>
                </div>
            </div>
        </div>
    );
}
